import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' }
});

// When the user is browsing the staff PWA (any /staff/* route), automatically
// attach their JWT so backend permission enforcement runs against THEM rather
// than the legacy unauth admin path. On the regular admin app the token is
// not attached so the master-password flow keeps working unchanged.
api.interceptors.request.use((config) => {
  if (typeof window === 'undefined') return config;
  const onStaff = window.location.pathname.startsWith('/staff/');
  config.headers = config.headers || {};
  if (onStaff) {
    const token = localStorage.getItem('staff_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } else {
    // Dashboard side — attach the per-user JWT (Phase 2 multi-user auth)
    // so backend endpoints (e.g. board-messages, activity log) can identify
    // who's calling. The legacy `emergeontop` master gate still works for
    // any endpoint that doesn't require an `app_role`.
    const token = localStorage.getItem('app_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Properties
export const getProperties = () => api.get('/properties').then(r => r.data);
export const createProperty = (data) => api.post('/properties', data).then(r => r.data);
export const updateProperty = (id, data) => api.put(`/properties/${id}`, data).then(r => r.data);
export const deleteProperty = (id) => api.delete(`/properties/${id}`).then(r => r.data);

// Units
export const getUnits = (propertyId) => {
  const params = propertyId ? { property_id: propertyId } : {};
  return api.get('/units', { params }).then(r => r.data);
};
export const createUnit = (data) => api.post('/units', data).then(r => r.data);
export const updateUnit = (id, data) => api.put(`/units/${id}`, data).then(r => r.data);
export const deleteUnit = (id) => api.delete(`/units/${id}`).then(r => r.data);

// AC Filters
export const getAcFilters = () => api.get('/ac-filters').then(r => r.data);
export const logAcFilterChange = (data) => api.post('/ac-filters/log-change', data).then(r => r.data);


// Tenants
export const getTenants = (params = {}) => api.get('/tenants', { params }).then(r => r.data);
export const getTenant = (id) => api.get(`/tenants/${id}`).then(r => r.data);
export const createTenant = (data) => api.post('/tenants', data).then(r => r.data);
export const updateTenant = (id, data) => api.put(`/tenants/${id}`, data).then(r => r.data);
export const deleteTenant = (id) => api.delete(`/tenants/${id}`).then(r => r.data);
export const confirmMoveout = (id) => api.post(`/tenants/${id}/confirm-moveout`).then(r => r.data);
export const extendTenantMonth = (id) => api.post(`/tenants/${id}/extend-month`).then(r => r.data);
export const getPendingMoveouts = () => api.get('/tenants/pending-moveouts').then(r => r.data);

// Leads
export const getLeads = () => api.get('/leads').then(r => r.data);
export const getLead = (id) => api.get(`/leads/${id}`).then(r => r.data);
export const createLead = (data) => api.post('/leads', data).then(r => r.data);
export const updateLead = (id, data) => api.put(`/leads/${id}`, data).then(r => r.data);
export const setLeadContactLog = (id, data) => api.put(`/leads/${id}/contact-log`, data).then(r => r.data);
export const logLeadContact = (id, channel) => api.post(`/leads/${id}/log-contact`, { channel }).then(r => r.data);
export const undoLeadContact = (id, channel, date) =>
  api.delete(`/leads/${id}/log-contact`, { params: { channel, date } }).then(r => r.data);
export const deleteLead = (id) => api.delete(`/leads/${id}`).then(r => r.data);
export const setLeadStatuses = (id, statuses) => api.put(`/leads/${id}/statuses`, { statuses }).then(r => r.data);
export const setLeadStrength = (id, lead_strength) => api.put(`/leads/${id}/strength`, { lead_strength }).then(r => r.data);
export const moveLeadToQuick = (id) => api.post(`/leads/${id}/move-to-quick`).then(r => r.data);
export const moveQuickToCurrent = (id) => api.post(`/quick-leads/${id}/move-to-current`).then(r => r.data);

// Quick Leads (Spreadsheet)
export const getQuickLeads = () => api.get('/quick-leads').then(r => r.data);
export const createQuickLead = (data) => api.post('/quick-leads', data).then(r => r.data);
export const updateQuickLead = (id, data) => api.put(`/quick-leads/${id}`, data).then(r => r.data);
export const deleteQuickLead = (id) => api.delete(`/quick-leads/${id}`).then(r => r.data);
export const parseLeadFile = (formData) => api.post('/quick-leads/parse', formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
  timeout: 60000,
}).then(r => r.data);

// Website Leads (submissions from flexlivingsf.com public lead form)
export const getWebsiteLeads = (password) => api.get('/website-leads', { params: { password } }).then(r => r.data);
export const updateWebsiteLead = (id, data) => api.put(`/website-leads/${id}`, data).then(r => r.data);
export const deleteWebsiteLead = (id, password) => api.delete(`/website-leads/${id}`, { params: { password } }).then(r => r.data);

// Showings
export const addShowing = (leadId, data) => api.post(`/leads/${leadId}/showings`, data).then(r => r.data);
export const updateShowing = (leadId, showingId, data) => api.put(`/leads/${leadId}/showings/${showingId}`, data).then(r => r.data);
export const deleteShowing = (leadId, showingId) => api.delete(`/leads/${leadId}/showings/${showingId}`).then(r => r.data);
export const getUpcomingShowings = () => api.get('/showings/upcoming').then(r => r.data);

// People (assignees)
export const getPeople = () => api.get('/people').then(r => r.data);
export const createPerson = (data) => api.post('/people', data).then(r => r.data);

// Calculations
export const getIncome = (year) => api.get('/income', { params: { year } }).then(r => r.data);
export const getVacancy = (year) => api.get('/vacancy', { params: { year } }).then(r => r.data);
export const getVacantForward = () => api.get('/vacant-forward').then(r => r.data);

// Calendar timeline (segment-based)
export const getCalendarTimeline = (params = {}) => api.get('/calendar/timeline', { params }).then(r => r.data);

// Available units
export const getAvailableUnits = (startDate, endDate) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  return api.get('/available-units', { params }).then(r => r.data);
};

// Admin-only: all units for a date range, including those that conflict with
// current tenancies. Each entry carries `available` + (when blocked) a
// `conflict` object `{tenant_name, move_in_date, move_out_date}`. Never
// consumed by public pages.
export const getUnitsWithConflicts = (startDate, endDate) => {
  const params = { include_unavailable: true };
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  return api.get('/available-units', { params }).then(r => r.data);
};

// Notes
export const getDeletedNotes = () => api.get('/notes/deleted').then(r => r.data);
export const restoreNote = (id) => api.put(`/notes/${id}/restore`).then(r => r.data);

// List Items (Ari & Mendel)
export const createListItem = (data) => api.post('/list-items', data).then(r => r.data);
export const updateListItem = (id, data) => api.put(`/list-items/${id}`, data).then(r => r.data);
export const markListItemDone = (id, by) => api.put(`/list-items/${id}/mark-done?by=${encodeURIComponent(by)}`).then(r => r.data);
export const respondToListItem = (id, data) => api.post(`/list-items/${id}/respond`, data).then(r => r.data);
export const deleteListItem = (id) => api.delete(`/list-items/${id}`).then(r => r.data);

// Parking Spots
export const getParkingSpots = () => api.get('/parking-spots').then(r => r.data);
export const createParkingSpot = (data) => api.post('/parking-spots', data).then(r => r.data);
export const updateParkingSpot = (id, data) => api.put(`/parking-spots/${id}`, data).then(r => r.data);
export const deleteParkingSpot = (id) => api.delete(`/parking-spots/${id}`).then(r => r.data);

// Parking Assignments
export const getParkingAssignments = (params = {}) => api.get('/parking-assignments', { params }).then(r => r.data);
export const createParkingAssignment = (data) => api.post('/parking-assignments', data).then(r => r.data);
export const updateParkingAssignment = (id, data) => api.put(`/parking-assignments/${id}`, data).then(r => r.data);
export const deleteParkingAssignment = (id) => api.delete(`/parking-assignments/${id}`).then(r => r.data);

// Parking Timeline
export const getParkingTimeline = () => api.get('/parking/timeline').then(r => r.data);

// Door Codes
export const getDoorCodes = (params = {}) => api.get('/door-codes', { params }).then(r => r.data);
export const saveDoorCode = (data) => api.post('/door-codes', data).then(r => r.data);

// Login Accounts + Columns
export const getLoginColumns = () => api.get('/login-columns').then(r => r.data);
export const createLoginColumn = (data) => api.post('/login-columns', data).then(r => r.data);
export const updateLoginColumn = (id, data) => api.put(`/login-columns/${id}`, data).then(r => r.data);
export const deleteLoginColumn = (id, force = false) => api.delete(`/login-columns/${id}`, { params: { force } }).then(r => r.data);
export const changeColumnSecret = (id, data) => api.put(`/login-columns/${id}/secret`, data).then(r => r.data);
export const unlockColumn = (id, secret) => api.post(`/login-columns/${id}/unlock`, { secret }).then(r => r.data);
export const getLoginAccounts = (unlockTokens = []) => api.get('/login-accounts', { params: { unlock_tokens: unlockTokens.join(',') } }).then(r => r.data);
export const createLoginAccount = (data, unlockTokens = []) => api.post('/login-accounts', data, { params: { unlock_tokens: unlockTokens.join(',') } }).then(r => r.data);
export const updateLoginAccount = (id, data, unlockTokens = []) => api.put(`/login-accounts/${id}`, data, { params: { unlock_tokens: unlockTokens.join(',') } }).then(r => r.data);
export const deleteLoginAccount = (id, unlockTokens = []) => api.delete(`/login-accounts/${id}`, { params: { unlock_tokens: unlockTokens.join(',') } }).then(r => r.data);

// Marketing Links
export const getMarketingLinks = (params = {}) => api.get('/marketing-links', { params }).then(r => r.data);
export const saveMarketingLink = (data) => api.post('/marketing-links', data).then(r => r.data);

// PIN Management
export const getPinStatus = () => api.get('/pins/status').then(r => r.data);
export const setPin = (data) => api.post('/pins/set', data).then(r => r.data);
export const verifyPin = (data) => api.post('/pins/verify', data).then(r => r.data);

// Move In / Move Out
export const getMoveInsOuts = () => api.get('/move-ins-outs').then(r => r.data);

// Housekeepers
export const getHousekeepers = (params = {}) => api.get('/housekeepers', { params }).then(r => r.data);
export const createHousekeeper = (data) => api.post('/housekeepers', data).then(r => r.data);
export const updateHousekeeper = (id, data) => api.put(`/housekeepers/${id}`, data).then(r => r.data);
export const deleteHousekeeper = (id) => api.delete(`/housekeepers/${id}`).then(r => r.data);

// Housekeeping Leads
export const getHousekeepingLeads = (params = {}) => api.get('/housekeeping-leads', { params }).then(r => r.data);
export const createHousekeepingLead = (data) => api.post('/housekeeping-leads', data).then(r => r.data);
export const updateHousekeepingLead = (id, data) => api.put(`/housekeeping-leads/${id}`, data).then(r => r.data);
export const deleteHousekeepingLead = (id) => api.delete(`/housekeeping-leads/${id}`).then(r => r.data);

// Cleaning Records
export const getCleaningRecords = (params = {}) => api.get('/cleaning-records', { params }).then(r => r.data);
export const updateCleaningRecord = (id, data) => api.put(`/cleaning-records/${id}`, data).then(r => r.data);

// Manual Cleanings
export const getManualCleanings = () => api.get('/manual-cleanings').then(r => r.data);
export const createManualCleaning = (data) => api.post('/manual-cleanings', data).then(r => r.data);
export const updateManualCleaning = (id, data) => api.put(`/manual-cleanings/${id}`, data).then(r => r.data);
export const deleteManualCleaning = (id) => api.delete(`/manual-cleanings/${id}`).then(r => r.data);

// Deposits
export const getCurrentDeposits = () => api.get('/deposits/current').then(r => r.data);
export const getPastDeposits = () => api.get('/deposits/past').then(r => r.data);
export const returnDeposit = (tenantId, data) => api.post(`/tenants/${tenantId}/return-deposit`, data).then(r => r.data);
export const getLandlordDeposits = () => api.get('/landlord-deposits').then(r => r.data);
export const updateLandlordDeposit = (unitId, amount) => api.put(`/landlord-deposits/${unitId}?amount=${amount}`).then(r => r.data);

// Misc Charges
export const createMiscCharge = (tenantId, data) => api.post(`/tenants/${tenantId}/misc-charges`, data).then(r => r.data);
export const getMiscCharges = (params = {}) => api.get('/misc-charges', { params }).then(r => r.data);
export const deleteMiscCharge = (id) => api.delete(`/misc-charges/${id}`).then(r => r.data);

// Rent Tracking
export const getRentTracking = (params = {}) => api.get('/rent-tracking', { params }).then(r => r.data);
export const updateRentPayment = (tenantId, data, year, month) => api.put(`/rent-tracking/${tenantId}?year=${year}&month=${month}`, data).then(r => r.data);
export const getTenantRentSchedule = (tenantId) =>
  api.get(`/rent-tracking/tenant/${tenantId}/schedule`).then(r => r.data);
export const updateTenantRentScheduleMonth = (tenantId, year, month, payload) =>
  api.put(`/rent-tracking/tenant/${tenantId}/schedule/${year}/${month}`, payload).then(r => r.data);
export const bulkUpdateTenantDueDay = (tenantId, dueDay, scope) =>
  api.put(`/rent-tracking/tenant/${tenantId}/schedule/bulk-due-day`, { due_day: dueDay, scope }).then(r => r.data);

// Maintenance Personnel
export const getMaintenancePersonnel = (params = {}) => api.get('/maintenance-personnel', { params }).then(r => r.data);
export const createMaintenancePersonnel = (data) => api.post('/maintenance-personnel', data).then(r => r.data);
export const updateMaintenancePersonnel = (id, data) => api.put(`/maintenance-personnel/${id}`, data).then(r => r.data);
export const deleteMaintenancePersonnel = (id) => api.delete(`/maintenance-personnel/${id}`).then(r => r.data);

// Maintenance Requests
export const getMaintenanceRequests = (params = {}) => api.get('/maintenance-requests', { params }).then(r => r.data);
export const createMaintenanceRequest = (data) => api.post('/maintenance-requests', data).then(r => r.data);
export const updateMaintenanceRequest = (id, data) => api.put(`/maintenance-requests/${id}`, data).then(r => r.data);
export const deleteMaintenanceRequest = (id) => api.delete(`/maintenance-requests/${id}`).then(r => r.data);

// Message Board - Members
export const getBoardMembers = (params = {}) => api.get('/board-members', { params }).then(r => r.data);
export const createBoardMember = (data) => api.post('/board-members', data).then(r => r.data);
export const archiveBoardMember = (id) => api.put(`/board-members/${id}/archive`).then(r => r.data);
export const unarchiveBoardMember = (id) => api.put(`/board-members/${id}/unarchive`).then(r => r.data);

// Message Board - Messages
export const getBoardMessages = (params = {}) => api.get('/board-messages', { params }).then(r => r.data);
export const getPendingBoardMessages = (params = {}) => api.get('/board-messages/pending', { params }).then(r => r.data);
export const getClosedBoardMessages = (params = {}) => api.get('/board-messages/closed', { params }).then(r => r.data);
export const activatePendingBoardMessages = () => api.post('/board-messages/activate-pending').then(r => r.data);
export const createBoardMessage = (data) => api.post('/board-messages', data).then(r => r.data);
export const updateBoardMessage = (id, data) => api.put(`/board-messages/${id}`, data).then(r => r.data);
export const respondToBoardMessage = (id, data) => api.post(`/board-messages/${id}/respond`, data).then(r => r.data);
export const closeBoardMessage = (id, by) => api.put(`/board-messages/${id}/close?by=${encodeURIComponent(by)}`).then(r => r.data);
export const snoozeBoardMessage = (id, until) => api.put(`/board-messages/${id}/snooze?until=${encodeURIComponent(until)}`).then(r => r.data);
export const deleteBoardMessage = (id) => api.delete(`/board-messages/${id}`).then(r => r.data);
export const getBoardUnreadCount = () => api.get('/board-messages/unread-count').then(r => r.data);
export const markBoardSeen = () => api.post('/board-messages/mark-seen').then(r => r.data);

// Dashboard
export const getDashboardOverview = () => api.get('/dashboard/overview').then(r => r.data);
export const getVacancyRateBreakdown = () => api.get('/dashboard/vacancy-rate-breakdown').then(r => r.data);

// Furniture Projects
export const getFurnitureProjects = ({ includeArchived = false } = {}) =>
  api.get('/furniture-projects', { params: includeArchived ? { include_archived: true } : {} }).then(r => r.data);
export const createFurnitureProject = (data) => api.post('/furniture-projects', data).then(r => r.data);
export const getFurnitureProject = (id) => api.get(`/furniture-projects/${id}`).then(r => r.data);
export const updateFurnitureProject = (id, data) => api.put(`/furniture-projects/${id}`, data).then(r => r.data);
export const deleteFurnitureProject = (id) => api.delete(`/furniture-projects/${id}`).then(r => r.data);
export const setFurnitureProjectArchived = (id, archived) =>
  api.post(`/furniture-projects/${id}/archive`, { archived }).then(r => r.data);
export const getFurnitureSubProjects = (masterId, { includeArchived = false } = {}) =>
  api.get(`/furniture-projects/${masterId}/sub-projects`, { params: includeArchived ? { include_archived: true } : {} }).then(r => r.data);
export const duplicateFurnitureProject = (id, data = {}) =>
  api.post(`/furniture-projects/${id}/duplicate`, data).then(r => r.data);
export const addFurnitureRoom = (projectId, data) => api.post(`/furniture-projects/${projectId}/rooms`, data).then(r => r.data);
export const getFurnitureRoomTemplates = () => api.get('/furniture-projects/templates/rooms').then(r => r.data);
/**
 * @param {string} roomType
 * @param {{items:string[], essentials:string[], suggestions:{items:string[], essentials:string[]}}} payload
 */
export const updateFurnitureRoomTemplate = (roomType, payload) =>
  api.put(`/furniture-projects/templates/rooms/${encodeURIComponent(roomType)}`, payload).then(r => r.data);
export const createFurnitureRoomTemplate = (roomType) =>
  api.post('/furniture-projects/templates/rooms', { room_type: roomType }).then(r => r.data);
export const deleteFurnitureRoomTemplate = (roomType) =>
  api.delete(`/furniture-projects/templates/rooms/${encodeURIComponent(roomType)}`).then(r => r.data);
export const getFurnitureEssentialLinks = () =>
  api.get('/furniture-projects/templates/essential-links').then(r => r.data);
export const upsertFurnitureEssentialLinks = (name, payload) =>
  // Accepts { links, category, notes } — any subset.
  api.put(`/furniture-projects/templates/essential-links/${encodeURIComponent(name)}`, payload).then(r => r.data);
export const deleteFurnitureEssentialLinks = (name) =>
  api.delete(`/furniture-projects/templates/essential-links/${encodeURIComponent(name)}`).then(r => r.data);
export const renameFurnitureEssential = (oldName, newName) =>
  api.post(`/furniture-projects/templates/essential-links/${encodeURIComponent(oldName)}/rename`, { new_name: newName }).then(r => r.data);
export const getFurnitureEssentialCategories = () =>
  api.get('/furniture-projects/templates/essential-categories').then(r => r.data);
export const createFurnitureEssentialCategory = (name) =>
  api.post('/furniture-projects/templates/essential-categories', { name }).then(r => r.data);
export const renameFurnitureEssentialCategory = (catId, name) =>
  api.put(`/furniture-projects/templates/essential-categories/${catId}`, { name }).then(r => r.data);
export const deleteFurnitureEssentialCategory = (catId, moveTo) =>
  api.delete(`/furniture-projects/templates/essential-categories/${catId}`, { params: moveTo ? { move_to: moveTo } : {} }).then(r => r.data);
export const reorderFurnitureEssentialCategories = (orderedIds) =>
  api.put('/furniture-projects/templates/essential-categories/reorder', { ordered_ids: orderedIds }).then(r => r.data);

// ── Items catalog (lookup-only — name + category) ─────────────
export const getFurnitureItemsCatalog = () =>
  api.get('/furniture-projects/templates/items-catalog').then(r => r.data);
export const upsertFurnitureCatalogItem = (name, payload) =>
  api.put(`/furniture-projects/templates/items-catalog/${encodeURIComponent(name)}`, payload).then(r => r.data);
export const deleteFurnitureCatalogItem = (name) =>
  api.delete(`/furniture-projects/templates/items-catalog/${encodeURIComponent(name)}`).then(r => r.data);
export const renameFurnitureCatalogItem = (oldName, newName) =>
  api.post(`/furniture-projects/templates/items-catalog/${encodeURIComponent(oldName)}/rename`, { new_name: newName }).then(r => r.data);
export const getFurnitureItemCategories = () =>
  api.get('/furniture-projects/templates/item-categories').then(r => r.data);
export const createFurnitureItemCategory = (name) =>
  api.post('/furniture-projects/templates/item-categories', { name }).then(r => r.data);
export const renameFurnitureItemCategory = (catId, name) =>
  api.put(`/furniture-projects/templates/item-categories/${catId}`, { name }).then(r => r.data);
export const deleteFurnitureItemCategory = (catId, moveTo) =>
  api.delete(`/furniture-projects/templates/item-categories/${catId}`, { params: moveTo ? { move_to: moveTo } : {} }).then(r => r.data);
export const syncRoomDefaultLinks = (projectId, roomId) =>
  api.post(`/furniture-projects/${projectId}/rooms/${roomId}/sync-default-links`).then(r => r.data);
export const updateFurnitureRoom = (projectId, roomId, data) => api.put(`/furniture-projects/${projectId}/rooms/${roomId}`, data).then(r => r.data);
export const deleteFurnitureRoom = (projectId, roomId) => api.delete(`/furniture-projects/${projectId}/rooms/${roomId}`).then(r => r.data);
export const addFurnitureItem = (projectId, roomId, data) => api.post(`/furniture-projects/${projectId}/rooms/${roomId}/items`, data).then(r => r.data);
export const updateFurnitureItem = (projectId, roomId, itemId, data) => api.put(`/furniture-projects/${projectId}/rooms/${roomId}/items/${itemId}`, data).then(r => r.data);
export const deleteFurnitureItem = (projectId, roomId, itemId) => api.delete(`/furniture-projects/${projectId}/rooms/${roomId}/items/${itemId}`).then(r => r.data);
export const getFurnitureProjectByShareToken = (token) => api.get(`/furniture-projects/share/${token}`).then(r => r.data);
export const getFurnitureProjectByViewToken = (token) => api.get(`/furniture-projects/view/${token}`).then(r => r.data);

// Designer profile + hours log. Either projectId (admin) or shareToken (designer)
// must be provided; both hit the same data.
const _designerBase = ({ projectId, shareToken }) =>
  shareToken ? `/furniture-projects/share/${shareToken}/designer` : `/furniture-projects/${projectId}/designer`;
export const getDesignerProfile = (ctx) => api.get(_designerBase(ctx)).then(r => r.data);
export const updateDesignerProfile = (ctx, data) => api.put(_designerBase(ctx), data).then(r => r.data);
export const addDesignerHours = (ctx, data) => api.post(`${_designerBase(ctx)}/hours`, data).then(r => r.data);
export const deleteDesignerHours = (ctx, entryId) => api.delete(`${_designerBase(ctx)}/hours/${entryId}`).then(r => r.data);
export const updateFurnitureItemViaShare = (token, roomId, itemId, data) => api.put(`/furniture-projects/share/${token}/rooms/${roomId}/items/${itemId}`, data).then(r => r.data);

export const reorderFurnitureRooms = (projectId, roomIds) =>
  api.put(`/furniture-projects/${projectId}/rooms/reorder`, { room_ids: roomIds }).then(r => r.data);
export const reorderFurnitureRoomsViaShare = (token, roomIds) =>
  api.put(`/furniture-projects/share/${token}/rooms/reorder`, { room_ids: roomIds }).then(r => r.data);
export const reorderFurnitureItems = (projectId, roomId, itemIds) =>
  api.put(`/furniture-projects/${projectId}/rooms/${roomId}/items/reorder`, { item_ids: itemIds }).then(r => r.data);
export const reorderFurnitureItemsViaShare = (token, roomId, itemIds) =>
  api.put(`/furniture-projects/share/${token}/rooms/${roomId}/items/reorder`, { item_ids: itemIds }).then(r => r.data);

// Furniture - Image Upload
export const uploadFurnitureItemImage = (projectId, roomId, itemId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/furniture-projects/${projectId}/rooms/${roomId}/items/${itemId}/upload-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  }).then(r => r.data);
};
export const uploadFurnitureItemImageViaShare = (token, roomId, itemId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/furniture-projects/share/${token}/rooms/${roomId}/items/${itemId}/upload-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  }).then(r => r.data);
};

// Furniture - Mood Board
export const getFurnitureMoodBoard = (projectId, roomId) => api.get(`/furniture-projects/${projectId}/rooms/${roomId}/mood-board`).then(r => r.data);
export const getFurnitureMoodBoardViaShare = (token, roomId) => api.get(`/furniture-projects/share/${token}/rooms/${roomId}/mood-board`).then(r => r.data);
export const getFurnitureMoodBoardViaView = (token, roomId) => api.get(`/furniture-projects/view/${token}/rooms/${roomId}/mood-board`).then(r => r.data);

// Furniture - Serve image file
export const getFurnitureImageUrl = (filePath) => `${API}/furniture-projects/files/${filePath}`;

// Furniture - Receipts
export const listFurnitureReceipts = (projectId, itemId) =>
  api.get(`/furniture-projects/${projectId}/receipts`, { params: itemId ? { item_id: itemId } : {} }).then(r => r.data);

export const uploadFurnitureReceipt = (projectId, { file, itemIds = [], vendor = '', amount = '', note = '' }) => {
  const formData = new FormData();
  formData.append('file', file);
  if (itemIds.length) formData.append('item_ids', itemIds.join(','));
  if (vendor) formData.append('vendor', vendor);
  if (amount !== '' && amount != null) formData.append('amount', String(amount));
  if (note) formData.append('note', note);
  return api.post(`/furniture-projects/${projectId}/receipts`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  }).then(r => r.data);
};

export const updateFurnitureReceipt = (projectId, receiptId, data) =>
  api.patch(`/furniture-projects/${projectId}/receipts/${receiptId}`, data).then(r => r.data);

export const deleteFurnitureReceipt = (projectId, receiptId) =>
  api.delete(`/furniture-projects/${projectId}/receipts/${receiptId}`).then(r => r.data);

// Furniture - Receipt Processing (AI parsing)
export const processReceipt = (file, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/furniture-projects/receipt-processing', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
    onUploadProgress: onProgress,
  }).then(r => r.data);
};
export const listProcessedReceipts = () =>
  api.get('/furniture-projects/receipt-processing').then(r => r.data);
export const getProcessedReceipt = (id) =>
  api.get(`/furniture-projects/receipt-processing/${id}`).then(r => r.data);
export const updateProcessedReceipt = (id, data) =>
  api.patch(`/furniture-projects/receipt-processing/${id}`, data).then(r => r.data);
export const deleteProcessedReceipt = (id) =>
  api.delete(`/furniture-projects/receipt-processing/${id}`).then(r => r.data);
export const getAllocationTargets = () =>
  api.get('/furniture-projects/receipt-processing/allocation-targets').then(r => r.data);
export const commitProcessedReceipt = (id) =>
  api.post(`/furniture-projects/receipt-processing/${id}/commit`).then(r => r.data);

// Financial Health
export const getFinancialHealth = () => api.get('/financial-health').then(r => r.data);
export const updateUnitCosts = (unitId, data) => api.put(`/financial-health/costs/${unitId}`, data).then(r => r.data);
export const addPotentialIncome = (unitId, year, month, data) => api.post(`/financial-health/potential/${unitId}/${year}/${month}`, data).then(r => r.data);
export const updatePotentialIncome = (id, data) => api.put(`/financial-health/potential/${id}`, data).then(r => r.data);
export const deletePotentialIncome = (id) => api.delete(`/financial-health/potential/${id}`).then(r => r.data);

// Furniture - Mood Board Drafts
export const getFurnitureDrafts = (projectId, roomId) => api.get(`/furniture-projects/${projectId}/rooms/${roomId}/drafts`).then(r => r.data);
export const saveFurnitureDraft = (projectId, roomId, data) => api.post(`/furniture-projects/${projectId}/rooms/${roomId}/drafts`, data).then(r => r.data);
export const deleteFurnitureDraft = (projectId, roomId, draftId) => api.delete(`/furniture-projects/${projectId}/rooms/${roomId}/drafts/${draftId}`).then(r => r.data);
export const getFurnitureDraftsViaShare = (token, roomId) => api.get(`/furniture-projects/share/${token}/rooms/${roomId}/drafts`).then(r => r.data);
export const saveFurnitureDraftViaShare = (token, roomId, data) => api.post(`/furniture-projects/share/${token}/rooms/${roomId}/drafts`, data).then(r => r.data);
export const deleteFurnitureDraftViaShare = (token, roomId, draftId) => api.delete(`/furniture-projects/share/${token}/rooms/${roomId}/drafts/${draftId}`).then(r => r.data);

// Furniture - Room BG
export const uploadFurnitureRoomBg = (projectId, roomId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/furniture-projects/${projectId}/rooms/${roomId}/upload-bg`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  }).then(r => r.data);
};
export const clearFurnitureRoomBg = (projectId, roomId) => api.delete(`/furniture-projects/${projectId}/rooms/${roomId}/bg`).then(r => r.data);
export const uploadFurnitureRoomBgViaShare = (token, roomId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/furniture-projects/share/${token}/rooms/${roomId}/upload-bg`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  }).then(r => r.data);
};
export const clearFurnitureRoomBgViaShare = (token, roomId) => api.delete(`/furniture-projects/share/${token}/rooms/${roomId}/bg`).then(r => r.data);

// Furniture - Mood Boards (new simplified model: list of uploaded images per room)
export const listFurnitureMoodboards = (projectId, roomId) =>
  api.get(`/furniture-projects/${projectId}/rooms/${roomId}/moodboards`).then(r => r.data);
export const listFurnitureMoodboardsViaShare = (token, roomId) =>
  api.get(`/furniture-projects/share/${token}/rooms/${roomId}/moodboards`).then(r => r.data);
export const listFurnitureMoodboardsViaView = (token, roomId) =>
  api.get(`/furniture-projects/view/${token}/rooms/${roomId}/moodboards`).then(r => r.data);

const _uploadMb = (url, file, { name, combinedFrom } = {}) => {
  const fd = new FormData();
  fd.append('file', file);
  if (name) fd.append('name', name);
  if (combinedFrom?.length) fd.append('combined_from', combinedFrom.join(','));
  return api.post(url, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  }).then(r => r.data);
};
export const uploadFurnitureMoodboard = (projectId, roomId, file, opts) =>
  _uploadMb(`/furniture-projects/${projectId}/rooms/${roomId}/moodboards`, file, opts);
export const uploadFurnitureMoodboardViaShare = (token, roomId, file, opts) =>
  _uploadMb(`/furniture-projects/share/${token}/rooms/${roomId}/moodboards`, file, opts);

export const deleteFurnitureMoodboard = (projectId, roomId, mbId) =>
  api.delete(`/furniture-projects/${projectId}/rooms/${roomId}/moodboards/${mbId}`).then(r => r.data);
export const deleteFurnitureMoodboardViaShare = (token, roomId, mbId) =>
  api.delete(`/furniture-projects/share/${token}/rooms/${roomId}/moodboards/${mbId}`).then(r => r.data);

export const reorderFurnitureMoodboards = (projectId, roomId, orderedIds) =>
  api.put(`/furniture-projects/${projectId}/rooms/${roomId}/moodboards/reorder`, { ordered_ids: orderedIds }).then(r => r.data);
export const reorderFurnitureMoodboardsViaShare = (token, roomId, orderedIds) =>
  api.put(`/furniture-projects/share/${token}/rooms/${roomId}/moodboards/reorder`, { ordered_ids: orderedIds }).then(r => r.data);

/* ── Floor plans (mirror of mood boards, different room field/route) ── */
export const listFurnitureFloorplans = (projectId, roomId) =>
  api.get(`/furniture-projects/${projectId}/rooms/${roomId}/floorplans`).then(r => r.data);
export const listFurnitureFloorplansViaShare = (token, roomId) =>
  api.get(`/furniture-projects/share/${token}/rooms/${roomId}/floorplans`).then(r => r.data);
export const listFurnitureFloorplansViaView = (token, roomId) =>
  api.get(`/furniture-projects/view/${token}/rooms/${roomId}/floorplans`).then(r => r.data);
export const uploadFurnitureFloorplan = (projectId, roomId, file, opts) =>
  _uploadMb(`/furniture-projects/${projectId}/rooms/${roomId}/floorplans`, file, opts);
export const uploadFurnitureFloorplanViaShare = (token, roomId, file, opts) =>
  _uploadMb(`/furniture-projects/share/${token}/rooms/${roomId}/floorplans`, file, opts);
export const deleteFurnitureFloorplan = (projectId, roomId, fpId) =>
  api.delete(`/furniture-projects/${projectId}/rooms/${roomId}/floorplans/${fpId}`).then(r => r.data);
export const deleteFurnitureFloorplanViaShare = (token, roomId, fpId) =>
  api.delete(`/furniture-projects/share/${token}/rooms/${roomId}/floorplans/${fpId}`).then(r => r.data);
export const reorderFurnitureFloorplans = (projectId, roomId, orderedIds) =>
  api.put(`/furniture-projects/${projectId}/rooms/${roomId}/floorplans/reorder`, { ordered_ids: orderedIds }).then(r => r.data);
export const reorderFurnitureFloorplansViaShare = (token, roomId, orderedIds) =>
  api.put(`/furniture-projects/share/${token}/rooms/${roomId}/floorplans/reorder`, { ordered_ids: orderedIds }).then(r => r.data);

// Furniture - Cost Breakdowns (shareable summaries)
export const listBreakdowns = (projectId) =>
  api.get(`/furniture-projects/${projectId}/breakdowns`).then(r => r.data);
export const createBreakdown = (projectId, data) =>
  api.post(`/furniture-projects/${projectId}/breakdowns`, data).then(r => r.data);
export const getBreakdownAdmin = (projectId, breakdownId) =>
  api.get(`/furniture-projects/${projectId}/breakdowns/${breakdownId}`).then(r => r.data);
export const updateBreakdown = (projectId, breakdownId, data) =>
  api.put(`/furniture-projects/${projectId}/breakdowns/${breakdownId}`, data).then(r => r.data);
export const deleteBreakdown = (projectId, breakdownId) =>
  api.delete(`/furniture-projects/${projectId}/breakdowns/${breakdownId}`).then(r => r.data);
export const getPublicBreakdown = (token) =>
  api.get(`/breakdowns/${token}`).then(r => r.data);

export default api;
